// Client wrapper over the `plan-week` edge function.
//
// Assembles the compact request body (lean pantry via buildAIContext —
// which keeps the SAFETY-CRITICAL allergen exclusions — plus the book
// digest and canonical roster), invokes the function, surfaces
// token-denial messages the same way generateRecipe.js does, and runs
// the client-side allergen re-check over returned concepts (belt and
// braces — the server already sweeps).

import { supabase } from "./supabase";
import { buildAIContext } from "./aiContext";
import { buildCanonicalRoster } from "./canonicalRoster";
import { sanitizeRequestBody } from "./sanitizeForJson";
import { findAllergyConflicts } from "../data/allergens";

/**
 * @param {object} opts
 * @param {"menu"|"reroll"} [opts.mode]
 * @param {object} opts.factors      — { source: { book: 0..1, mode }, goal, budget }
 * @param {Array}  opts.slots        — 21 slot requests { day, slot, enabled, locked? }
 * @param {number} opts.servings     — household servings per meal
 * @param {Array}  opts.pantry       — raw usePantry rows (shaped here)
 * @param {object} opts.profile      — useProfile row (dietary + allergies ride)
 * @param {Array}  opts.bookDigest   — buildBookDigest() rows
 * @param {Array}  [opts.avoidTitles]
 * @param {Array}  [opts.rerollSlots]   — reroll mode: [{ day, slot }]
 * @param {Array}  [opts.currentTitles] — reroll mode: titles staying in the plan
 * @returns {Promise<{ slots: Array, missing: Array, weekNote: string }>}
 */
export async function planWeek({
  mode = "menu",
  factors,
  slots,
  servings,
  pantry = [],
  profile = null,
  bookDigest = [],
  avoidTitles = [],
  rerollSlots = [],
  currentTitles = [],
} = {}) {
  const ai = buildAIContext({ pantry, profile, mode: "lean" });
  const canonicalRoster = buildCanonicalRoster();

  const body = sanitizeRequestBody({
    mode,
    factors,
    slots,
    household: { servings },
    pantry: ai.pantry,
    bookDigest,
    context: ai.context,
    canonicalRoster,
    avoidTitles,
    rerollSlots,
    currentTitles,
  });

  const { data, error } = await supabase.functions.invoke("plan-week", { body });

  if (error) {
    let detail = "";
    let tokenDenialMsg = "";
    const ctx = error.context;
    let text = "";
    try {
      if (ctx && typeof ctx.text === "function") text = await ctx.text();
      else if (typeof ctx?.body === "string") text = ctx.body;
      else if (ctx?.body && typeof ctx.body === "object") text = JSON.stringify(ctx.body);
    } catch {
      /* fall through to message */
    }
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error === "ai_tokens_exhausted" && parsed?.message) {
          tokenDenialMsg = String(parsed.message);
        }
        detail = parsed?.detail || parsed?.error || text;
      } catch {
        detail = text;
      }
    }
    if (tokenDenialMsg) throw new Error(tokenDenialMsg);
    console.error("[plan-week] edge fn failure", { error, detail });
    throw new Error(
      detail
        ? `Week planning failed: ${String(detail).slice(0, 300)}`
        : `Week planning failed: ${error.message || "unknown error"}`,
    );
  }

  const plan = data?.plan;
  if (!plan || !Array.isArray(plan.slots)) {
    throw new Error("Week planning failed: empty response");
  }

  // Client-side allergen re-check with the richer matcher. The server
  // swept with substring matching; findAllergyConflicts knows the
  // taxonomy (derivatives, alternate names). A hit turns the slot
  // into an error entry the review screen offers to reroll.
  const allergies = profile?.allergies;
  const checked = plan.slots.map((s) => {
    if (!s || s.error || !allergies) return s;
    const hay =
      s.source === "new" && s.concept
        ? [s.concept.title, s.concept.subtitle,
           ...(s.concept.estIngredients || []).map((e) => e?.name)].filter(Boolean).join(" ")
        : s.title || "";
    const conflicts = findAllergyConflicts(hay, allergies);
    return conflicts?.length
      ? { day: s.day, slot: s.slot, error: "allergen_conflict", title: s.title }
      : s;
  });

  return {
    slots: checked,
    missing: Array.isArray(plan.missing) ? plan.missing : [],
    weekNote: typeof plan.weekNote === "string" ? plan.weekNote : "",
  };
}
